import React from "react";
import {useMessages} from "i18n";
import {
	SPONSOR_POSTER_TYPES,
	SPONSOR_VIDEO_LIMITS,
	SPONSOR_VIDEO_MAX_MB,
	SPONSOR_VIDEO_TYPES,
	type SponsorPickedAsset,
} from "../sponsorOptions";
import {AssetPickerField} from "./AssetPickerField";
import {OptionsGrid} from "./SponsorOptionsPanel.styles";

/**
 * The VIDEO_LOOP's creative: one video and the still frame that stands in for it
 * (kitecorp/transferify-ui#1867).
 *
 * It sits in the CREATIVE step beside the multi-image picker it replaces, not in the poster settings
 * panel, and the placement is the argument. The options panel arranges the advertiser's type on top of
 * whatever is behind it; this is what is behind it. Keeping the two apart also keeps the sender's own
 * page-theme panel, which cannot buy a video at all, free of two controls that would do nothing there.
 *
 * The FORMATS come from the same lists the validators judge against, and the two bounds from the same
 * constants they compare to, so no `accept` and no hint can promise something an upload then refuses.
 *
 * Dumb by mandate (epic #26): two held assets, four callbacks, and a translator for the messages.
 */
export interface VideoCreativeFieldsProps {
	video: SponsorPickedAsset | null;
	poster: SponsorPickedAsset | null;
	onPickVideo: (file: File, opener: HTMLElement) => void;
	onRemoveVideo: () => void;
	onPickPoster: (file: File, opener: HTMLElement) => void;
	onRemovePoster: () => void;
	/** True while any upload is in flight; both pickers shut so two cannot race. */
	uploading: boolean;
	/** Already-translated message for one control, or `null`. */
	errorText: (field: "video" | "poster") => string | null;
}

/** The `accept` attribute for a picker, derived from the list its validator judges against. */
const acceptOf = (types: readonly string[]): string => types.join(",");

export function VideoCreativeFields({
	video, poster, onPickVideo, onRemoveVideo, onPickPoster, onRemovePoster, uploading, errorText,
}: VideoCreativeFieldsProps): React.ReactElement {
	const m = useMessages().sponsor.creative;

	return (
		<OptionsGrid>
			<AssetPickerField
				label={m.video.label}
				hint={m.video.hint(SPONSOR_VIDEO_MAX_MB, SPONSOR_VIDEO_LIMITS.maxSeconds)}
				accept={acceptOf(SPONSOR_VIDEO_TYPES)}
				addAria={m.video.addAria}
				replaceAria={m.video.replaceAria}
				addLabel={m.video.add}
				removeLabel={m.video.remove}
				url={video?.url ?? null}
				/*
				 * No alt, which is what switches the slot's preview to the FILENAME. A poster frame in a
				 * 52px box says nothing the name does not, and painting one would cost the advertiser a
				 * download of their own upload before they have even submitted it.
				 */
				alt={null}
				filename={video?.filename ?? null}
				disabled={uploading}
				busy={uploading}
				onPick={onPickVideo}
				onRemove={onRemoveVideo}
				error={errorText("video")}
			/>

			<AssetPickerField
				label={m.poster.label}
				hint={m.poster.hint}
				accept={acceptOf(SPONSOR_POSTER_TYPES)}
				addAria={m.poster.addAria}
				replaceAria={m.poster.replaceAria}
				addLabel={m.poster.add}
				removeLabel={m.poster.remove}
				url={poster?.url ?? null}
				// A picture, so the slot shows the picture: this one the advertiser does want to see, and
				// it is the frame most of their audience will be looking at.
				alt={m.poster.alt}
				filename={poster?.filename ?? null}
				disabled={uploading}
				busy={uploading}
				onPick={onPickPoster}
				onRemove={onRemovePoster}
				error={errorText("poster")}
			/>
		</OptionsGrid>
	);
}
